import { useEffect, useState, useRef, useCallback, useMemo } from 'react';

type LogEntry = {
  id: string;
  type: 'log' | 'info' | 'warn' | 'error';
  message: string;
  timestamp: Date;
  stack?: string;
};

type NetworkRequest = {
  id: string;
  timestamp: string;
  method: string;
  url: string;
  status?: number;
  duration?: number;
  error?: string;
  requestHeaders?: Record<string, string>;
  requestBody?: any;
  responseHeaders?: Record<string, string>;
  responseBody?: any;
  stack?: string;
};

type NetworkFilter = 'all' | 'success' | 'error' | 'pending';
type TabType = 'console' | 'network' | 'storage' | 'routing';

type RouteEntry = {
  id: string;
  timestamp: Date;
  path: string;
  routedTo?: string;
  deployment?: string;
};

const MAX_LOGS = 400;
const MAX_NETWORK_REQUESTS = 200;
const LOG_LEVELS: LogEntry['type'][] = ['log', 'info', 'warn', 'error'];
const DEV_CONSOLE_STORAGE_KEY = 'dev_console_enabled';
const NETWORK_REQUESTS_KEY = 'dev_console_network_requests';
const LOGS_STORAGE_KEY = 'dev_console_logs';

const deserializeLogs = (raw: string | null): LogEntry[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as Array<Omit<LogEntry, 'timestamp'> & { timestamp: string }>;
    return parsed.map((entry) => ({
      ...entry,
      timestamp: new Date(entry.timestamp),
    }));
  } catch {
    return [];
  }
};

const serializeLogs = (entries: LogEntry[]) =>
  JSON.stringify(entries.map((entry) => ({ ...entry, timestamp: entry.timestamp.toISOString() })));

export default function DevConsole() {
  const envEnabled = import.meta.env.DEV || import.meta.env.VITE_DEV_CONSOLE_ENABLED === 'true';
  const [isConsoleEnabled, setIsConsoleEnabled] = useState(() => {
    if (typeof window === 'undefined') {
      return envEnabled;
    }
    const stored = window.localStorage.getItem(DEV_CONSOLE_STORAGE_KEY);
    if (stored === null) {
      return envEnabled;
    }
    return stored === 'true';
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('console');
  const [isCapturing, setIsCapturing] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isAutoScroll, setIsAutoScroll] = useState(true);
  const [isGrouped, setIsGrouped] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilters, setLevelFilters] = useState<LogEntry['type'][]>(LOG_LEVELS);
  const [logs, setLogs] = useState<LogEntry[]>(() => {
    if (typeof window === 'undefined') {
      return [];
    }
    return deserializeLogs(window.sessionStorage.getItem(LOGS_STORAGE_KEY));
  });
  const [networkRequests, setNetworkRequests] = useState<NetworkRequest[]>([]);
  const [networkFilter, setNetworkFilter] = useState<NetworkFilter>('all');
  const [networkSearchQuery, setNetworkSearchQuery] = useState('');
  const [expandedRequestId, setExpandedRequestId] = useState<string | null>(null);
  const [storageSearchQuery, setStorageSearchQuery] = useState('');
  const [selectedStorageItem, setSelectedStorageItem] = useState<{
    scope: 'localStorage' | 'sessionStorage' | 'cookies';
    key: string;
    value: string;
  } | null>(null);
  const [routingHistory, setRoutingHistory] = useState<RouteEntry[]>([]);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const pendingLogsRef = useRef<LogEntry[]>([]);
  const isPausedRef = useRef(isPaused);
  const originalConsole = useRef({
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error,
  });
  const originalFetch = useRef<typeof fetch>();

  const appendLogs = useCallback((newEntries: LogEntry[]) => {
    setLogs((prev) => {
      const next = [...prev, ...newEntries];
      if (next.length > MAX_LOGS) {
        return next.slice(-MAX_LOGS);
      }
      return next;
    });
  }, []);

  const addNetworkRequest = useCallback((request: NetworkRequest) => {
    setNetworkRequests((prev) => {
      const next = [...prev, request];
      if (next.length > MAX_NETWORK_REQUESTS) {
        return next.slice(-MAX_NETWORK_REQUESTS);
      }
      // Also persist to sessionStorage
      try {
        sessionStorage.setItem(NETWORK_REQUESTS_KEY, JSON.stringify(next));
      } catch (e) {
        // Ignore storage errors
      }
      return next;
    });
  }, []);

  const updateNetworkRequest = useCallback((id: string, updates: Partial<NetworkRequest>) => {
    setNetworkRequests((prev) => {
      const next = prev.map(req => req.id === id ? { ...req, ...updates } : req);
      try {
        sessionStorage.setItem(NETWORK_REQUESTS_KEY, JSON.stringify(next));
      } catch (e) {
        // Ignore storage errors
      }
      return next;
    });
  }, []);

  // Load network requests from sessionStorage
  const refreshNetwork = useCallback(() => {
    try {
      const stored = sessionStorage.getItem(NETWORK_REQUESTS_KEY);
      if (stored) {
        setNetworkRequests(JSON.parse(stored));
      }
    } catch (e) {
      // Ignore parse errors
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'network') {
      refreshNetwork();
      const interval = setInterval(refreshNetwork, 1000);
      return () => clearInterval(interval);
    }
  }, [activeTab, refreshNetwork]);

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(DEV_CONSOLE_STORAGE_KEY, String(isConsoleEnabled));
    }
  }, [isConsoleEnabled]);

  useEffect(() => {
    if (!isConsoleEnabled) {
      setIsCapturing(false);
    }
  }, [isConsoleEnabled]);

  useEffect(() => {
    // Keyboard shortcut: Ctrl+Shift+K
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'K') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Intercept fetch for network monitoring
  useEffect(() => {
    if (!isConsoleEnabled) return;

    if (!originalFetch.current) {
      originalFetch.current = window.fetch;
    }

    window.fetch = async (...args) => {
      const [resource, config] = args;
      const url = typeof resource === 'string' ? resource : resource.url;
      const method = config?.method || 'GET';
      const startTime = Date.now();
      const requestId = `${Date.now()}-${Math.random()}`;

      // Capture request details
      const requestHeaders: Record<string, string> = {};
      if (config?.headers) {
        if (config.headers instanceof Headers) {
          config.headers.forEach((value, key) => {
            requestHeaders[key] = value;
          });
        } else if (typeof config.headers === 'object') {
          Object.assign(requestHeaders, config.headers);
        }
      }

      let requestBody: any = undefined;
      try {
        if (config?.body) {
          if (typeof config.body === 'string') {
            requestBody = JSON.parse(config.body);
          } else {
            requestBody = config.body.toString();
          }
        }
      } catch {
        requestBody = config?.body?.toString() || undefined;
      }

      // Add request to network log
      addNetworkRequest({
        id: requestId,
        timestamp: new Date().toISOString(),
        method,
        url,
        requestHeaders,
        requestBody,
      });

      try {
        const response = await originalFetch.current!.call(window, ...args);
        const duration = Date.now() - startTime;

        // Capture response details
        const responseHeaders: Record<string, string> = {};
        response.headers.forEach((value, key) => {
          responseHeaders[key] = value;
        });

        // Clone response to read body
        const clonedResponse = response.clone();
        let responseBody: any = undefined;
        try {
          const contentType = response.headers.get('content-type');
          if (contentType?.includes('application/json')) {
            responseBody = await clonedResponse.json();
          } else {
            responseBody = await clonedResponse.text();
          }
        } catch {
          // Ignore body read errors
        }

        updateNetworkRequest(requestId, {
          status: response.status,
          duration,
          responseHeaders,
          responseBody,
        });

        return response;
      } catch (error: any) {
        const duration = Date.now() - startTime;
        updateNetworkRequest(requestId, {
          error: error?.message || 'Network error',
          duration,
          stack: error?.stack,
        });
        throw error;
      }
    };

    return () => {
      if (originalFetch.current) {
        window.fetch = originalFetch.current;
      }
    };
  }, [isConsoleEnabled, addNetworkRequest, updateNetworkRequest]);

  // Track routing history and location changes
  useEffect(() => {
    if (!isConsoleEnabled) return;

    const addRouteEntry = (path: string, routedTo?: string) => {
      setRoutingHistory((prev) => {
        const entry: RouteEntry = {
          id: `${Date.now()}-${Math.random()}`,
          timestamp: new Date(),
          path,
          routedTo,
          deployment: window.location.hostname,
        };
        return [...prev, entry].slice(-50); // Keep last 50 routes
      });
    };

    // Track initial load
    addRouteEntry(window.location.pathname);

    // Track popstate (back/forward)
    const handlePopState = () => {
      addRouteEntry(window.location.pathname);
    };

    // Track pushState/replaceState
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function(...args) {
      const result = originalPushState.apply(this, args);
      addRouteEntry(window.location.pathname);
      return result;
    };

    history.replaceState = function(...args) {
      const result = originalReplaceState.apply(this, args);
      addRouteEntry(window.location.pathname);
      return result;
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isConsoleEnabled]);

  useEffect(() => {
    if (!isConsoleEnabled || !isCapturing) return;

    const addLog = (type: LogEntry['type'], args: any[]) => {
      const message = args
        .map((arg) => {
          if (typeof arg === 'object') {
            try {
              return JSON.stringify(arg, null, 2);
            } catch {
              return String(arg);
            }
          }
          return String(arg);
        })
        .join(' ');

      const entry: LogEntry = {
        id: `${Date.now()}-${Math.random()}`,
        type,
        message,
        timestamp: new Date(),
      };

      if (type === 'error' && args[0] instanceof Error) {
        entry.stack = args[0].stack;
      }

      if (isPausedRef.current) {
        pendingLogsRef.current.push(entry);
      } else {
        appendLogs([entry]);
      }
    };

    // Patch console methods
    console.log = (...args) => {
      originalConsole.current.log(...args);
      addLog('log', args);
    };

    console.info = (...args) => {
      originalConsole.current.info(...args);
      addLog('info', args);
    };

    console.warn = (...args) => {
      originalConsole.current.warn(...args);
      addLog('warn', args);
    };

    console.error = (...args) => {
      originalConsole.current.error(...args);
      addLog('error', args);
    };

    // Capture window errors
    const handleError = (event: ErrorEvent) => {
      addLog('error', [event.error || event.message]);
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      addLog('error', [`Unhandled Promise Rejection: ${event.reason}`]);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      // Restore original console methods
      console.log = originalConsole.current.log;
      console.info = originalConsole.current.info;
      console.warn = originalConsole.current.warn;
      console.error = originalConsole.current.error;
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [appendLogs, isCapturing, isConsoleEnabled]);

  useEffect(() => {
    if (isOpen && isAutoScroll && !isPaused && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isAutoScroll, isOpen, isPaused, logs]);

  useEffect(() => {
    if (!isPaused && pendingLogsRef.current.length > 0) {
      appendLogs(pendingLogsRef.current);
      pendingLogsRef.current = [];
    }
  }, [appendLogs, isPaused]);

  useEffect(() => {
    try {
      sessionStorage.setItem(LOGS_STORAGE_KEY, serializeLogs(logs));
    } catch {
      // Ignore storage errors
    }
  }, [logs]);

  const clearLogs = () => {
    setLogs([]);
    pendingLogsRef.current = [];
    sessionStorage.removeItem(LOGS_STORAGE_KEY);
  };

  const clearNetwork = () => {
    sessionStorage.removeItem(NETWORK_REQUESTS_KEY);
    setNetworkRequests([]);
  };

  const clearAllData = () => {
    if (confirm('Clear all data? This will clear cookies, localStorage, sessionStorage, and reload the page.')) {
      // Clear localStorage
      localStorage.clear();
      // Clear sessionStorage
      sessionStorage.clear();
      // Clear cookies
      document.cookie.split(";").forEach((c) => {
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      });
      // Reload
      window.location.reload();
    }
  };

  const getLogColor = (type: LogEntry['type']) => {
    switch (type) {
      case 'error':
        return '#ef4444';
      case 'warn':
        return '#f59e0b';
      case 'info':
        return '#3b82f6';
      default:
        return '#e2e8f0';
    }
  };

  const getLogIcon = (type: LogEntry['type']) => {
    switch (type) {
      case 'error':
        return '❌';
      case 'warn':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      default:
        return '▶';
    }
  };

  const getStatusColor = (status?: number) => {
    if (!status) return '#94a3b8';
    if (status >= 200 && status < 300) return '#10b981';
    if (status >= 300 && status < 400) return '#3b82f6';
    if (status >= 400 && status < 500) return '#f59e0b';
    return '#ef4444';
  };

  const getMethodColor = (method: string) => {
    switch (method.toUpperCase()) {
      case 'GET':
        return '#10b981';
      case 'POST':
        return '#3b82f6';
      case 'PUT':
        return '#f59e0b';
      case 'DELETE':
        return '#ef4444';
      case 'PATCH':
        return '#8b5cf6';
      default:
        return '#94a3b8';
    }
  };

  const filteredLogs = useMemo(() => {
    const activeLevels = new Set(levelFilters);
    const query = searchQuery.trim().toLowerCase();

    return logs.filter((log) => {
      if (!activeLevels.has(log.type)) return false;
      if (!query) return true;
      const content = [log.message, log.stack].filter(Boolean).join(' ').toLowerCase();
      return content.includes(query);
    });
  }, [levelFilters, logs, searchQuery]);

  const filteredNetworkRequests = useMemo(() => {
    const query = networkSearchQuery.trim().toLowerCase();

    return networkRequests.filter((req) => {
      // Filter by status
      if (networkFilter === 'success' && (!req.status || req.status >= 400 || req.error)) return false;
      if (networkFilter === 'error' && (!req.error && req.status && req.status < 400)) return false;
      if (networkFilter === 'pending' && (req.status || req.error)) return false;

      // Filter by search query
      if (!query) return true;
      const searchableContent = [
        req.method,
        req.url,
        req.status?.toString(),
        req.error,
      ].filter(Boolean).join(' ').toLowerCase();
      return searchableContent.includes(query);
    });
  }, [networkRequests, networkFilter, networkSearchQuery]);

  const displayLogs = useMemo(() => {
    if (!isGrouped) {
      return filteredLogs.map((log) => ({ ...log, count: 1 }));
    }

    return filteredLogs.reduce<Array<LogEntry & { count: number }>>((acc, log) => {
      const last = acc[acc.length - 1];
      if (last && last.type === log.type && last.message === log.message && last.stack === log.stack) {
        last.count += 1;
        return acc;
      }
      acc.push({ ...log, count: 1 });
      return acc;
    }, []);
  }, [filteredLogs, isGrouped]);

  const networkStats = useMemo(() => {
    const total = networkRequests.length;
    const success = networkRequests.filter(r => r.status && r.status >= 200 && r.status < 400 && !r.error).length;
    const errors = networkRequests.filter(r => r.error || (r.status && r.status >= 400)).length;
    const pending = networkRequests.filter(r => !r.status && !r.error).length;
    return { total, success, errors, pending };
  }, [networkRequests]);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'console':
        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#0f172a', color: '#e2e8f0', minHeight: 0 }}>
            <div style={{ padding: '10px', borderBottom: '1px solid #334155', background: '#1e293b', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', cursor: 'pointer', color: '#e2e8f0' }}>
                  <input
                    type="checkbox"
                    checked={isConsoleEnabled}
                    onChange={(e) => setIsConsoleEnabled(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  Enable
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', cursor: 'pointer', color: '#e2e8f0' }}>
                  <input
                    type="checkbox"
                    checked={isCapturing}
                    onChange={(e) => setIsCapturing(e.target.checked)}
                    disabled={!isConsoleEnabled}
                    style={{ cursor: 'pointer' }}
                  />
                  Capture
                </label>
                <button onClick={clearLogs} style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #334155', background: '#0f172a', color: '#e2e8f0', cursor: 'pointer', fontSize: '11px' }}>
                  Clear
                </button>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                  {filteredLogs.length}/{logs.length} logs • Max {MAX_LOGS}
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                {LOG_LEVELS.map((level) => {
                  const isActive = levelFilters.includes(level);
                  return (
                    <button
                      key={level}
                      onClick={() => {
                        setLevelFilters((prev) => (
                          prev.includes(level) ? prev.filter((item) => item !== level) : [...prev, level]
                        ));
                      }}
                      style={{
                        padding: '3px 8px',
                        borderRadius: '4px',
                        border: `1px solid ${isActive ? getLogColor(level) : '#334155'}`,
                        background: isActive ? '#0f172a' : 'transparent',
                        color: isActive ? getLogColor(level) : '#94a3b8',
                        fontSize: '11px',
                        cursor: 'pointer',
                        textTransform: 'uppercase',
                      }}
                    >
                      {level}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search logs..."
                  style={{
                    flex: '1 1 200px',
                    minWidth: '160px',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    border: '1px solid #334155',
                    background: '#0f172a',
                    color: '#e2e8f0',
                    fontSize: '12px',
                  }}
                />
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#94a3b8' }}>
                  <input type="checkbox" checked={isGrouped} onChange={(e) => setIsGrouped(e.target.checked)} />
                  Group
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#94a3b8' }}>
                  <input type="checkbox" checked={isAutoScroll} onChange={(e) => setIsAutoScroll(e.target.checked)} />
                  Auto-scroll
                </label>
                <button
                  onClick={() => setIsPaused((prev) => !prev)}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '4px',
                    border: '1px solid #334155',
                    background: isPaused ? '#ef4444' : '#0f172a',
                    color: '#e2e8f0',
                    fontSize: '11px',
                    cursor: 'pointer',
                  }}
                >
                  {isPaused ? 'Resume' : 'Pause'}
                </button>
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto', padding: '12px', fontFamily: 'monospace', fontSize: '13px' }}>
              {!isConsoleEnabled && (
                <div style={{ color: '#f59e0b', textAlign: 'center', padding: '12px', border: '1px solid #334155', borderRadius: '6px', marginBottom: '12px', background: '#1e293b' }}>
                  DevConsole capture is disabled by environment/feature flag.
                </div>
              )}
              {displayLogs.length === 0 ? (
                <div style={{ color: '#64748b', textAlign: 'center', padding: '24px' }}>
                  No console output yet. {!isCapturing && '(Capturing is paused)'}
                </div>
              ) : (
                displayLogs.map((log) => (
                  <div key={log.id} style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: '1px solid rgba(226, 232, 240, 0.1)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <span>{getLogIcon(log.type)}</span>
                      <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                        {log.timestamp.toLocaleTimeString()}
                      </span>
                      <span style={{ fontSize: '11px', fontWeight: '600', color: getLogColor(log.type), textTransform: 'uppercase' }}>
                        {log.type}
                      </span>
                      {log.count && log.count > 1 && (
                        <span style={{ fontSize: '10px', color: '#94a3b8', background: '#1e293b', padding: '1px 6px', borderRadius: '10px' }}>
                          x{log.count}
                        </span>
                      )}
                    </div>
                    <div style={{ paddingLeft: '24px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {log.message}
                    </div>
                    {log.stack && (
                      <div style={{ paddingLeft: '24px', marginTop: '4px', fontSize: '11px', color: '#94a3b8', whiteSpace: 'pre-wrap' }}>
                        {log.stack}
                      </div>
                    )}
                  </div>
                ))
              )}
              <div ref={logsEndRef} />
            </div>
          </div>
        );

      case 'network':
        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#0f172a', color: '#e2e8f0', minHeight: 0 }}>
            {/* Network filters and stats */}
            <div style={{ padding: '10px', borderBottom: '1px solid #334155', background: '#1e293b', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                <button
                  onClick={() => setNetworkFilter('all')}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '4px',
                    border: `1px solid ${networkFilter === 'all' ? '#3b82f6' : '#334155'}`,
                    background: networkFilter === 'all' ? '#1e40af' : 'transparent',
                    color: networkFilter === 'all' ? '#e2e8f0' : '#94a3b8',
                    fontSize: '11px',
                    cursor: 'pointer',
                  }}
                >
                  All ({networkStats.total})
                </button>
                <button
                  onClick={() => setNetworkFilter('success')}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '4px',
                    border: `1px solid ${networkFilter === 'success' ? '#10b981' : '#334155'}`,
                    background: networkFilter === 'success' ? '#065f46' : 'transparent',
                    color: networkFilter === 'success' ? '#e2e8f0' : '#94a3b8',
                    fontSize: '11px',
                    cursor: 'pointer',
                  }}
                >
                  Success ({networkStats.success})
                </button>
                <button
                  onClick={() => setNetworkFilter('error')}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '4px',
                    border: `1px solid ${networkFilter === 'error' ? '#ef4444' : '#334155'}`,
                    background: networkFilter === 'error' ? '#7f1d1d' : 'transparent',
                    color: networkFilter === 'error' ? '#e2e8f0' : '#94a3b8',
                    fontSize: '11px',
                    cursor: 'pointer',
                  }}
                >
                  Errors ({networkStats.errors})
                </button>
                <button
                  onClick={() => setNetworkFilter('pending')}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '4px',
                    border: `1px solid ${networkFilter === 'pending' ? '#f59e0b' : '#334155'}`,
                    background: networkFilter === 'pending' ? '#78350f' : 'transparent',
                    color: networkFilter === 'pending' ? '#e2e8f0' : '#94a3b8',
                    fontSize: '11px',
                    cursor: 'pointer',
                  }}
                >
                  Pending ({networkStats.pending})
                </button>
                <button onClick={clearNetwork} style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid #334155', background: '#1e293b', color: '#e2e8f0', cursor: 'pointer', fontSize: '11px', marginLeft: 'auto' }}>
                  Clear
                </button>
              </div>
              <input
                type="text"
                value={networkSearchQuery}
                onChange={(e) => setNetworkSearchQuery(e.target.value)}
                placeholder="Search URL, method, status..."
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  borderRadius: '4px',
                  border: '1px solid #334155',
                  background: '#0f172a',
                  color: '#e2e8f0',
                  fontSize: '12px',
                  marginTop: '8px',
                }}
              />
            </div>

            {/* Network requests list */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '12px',
                minHeight: 0,
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain',
                touchAction: 'pan-y',
              }}
            >
              {filteredNetworkRequests.length === 0 ? (
                <div style={{ color: '#64748b', textAlign: 'center', padding: '24px' }}>
                  No network requests found.
                </div>
              ) : (
                filteredNetworkRequests.map((req) => {
                  const isExpanded = expandedRequestId === req.id;
                  const isError = Boolean(req.error || (req.status && req.status >= 400));
                  const isSuccess = Boolean(req.status && req.status >= 200 && req.status < 400 && !req.error);

                  return (
                    <div key={req.id} style={{ marginBottom: '8px', border: '1px solid #334155', borderRadius: '6px', background: '#1e293b', overflow: 'hidden' }}>
                      {/* Request header - clickable */}
                      <div
                        onClick={() => setExpandedRequestId(isExpanded ? null : req.id)}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          background: isExpanded ? '#0f172a' : 'transparent',
                          transition: 'background 0.2s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span style={{ fontSize: '14px' }}>
                            {isError ? '❌' : isSuccess ? '✅' : '⏳'}
                          </span>
                          <span style={{ fontWeight: '700', fontSize: '11px', color: getMethodColor(req.method), background: '#0f172a', padding: '2px 8px', borderRadius: '4px' }}>
                            {req.method}
                          </span>
                          {req.status && (
                            <span style={{ fontSize: '11px', fontWeight: '600', color: getStatusColor(req.status) }}>
                              {req.status}
                            </span>
                          )}
                          {req.duration !== undefined && (
                            <span style={{ fontSize: '10px', color: '#94a3b8' }}>
                              {req.duration}ms
                            </span>
                          )}
                          <span style={{ fontSize: '10px', color: '#94a3b8', marginLeft: 'auto' }}>
                            {new Date(req.timestamp).toLocaleTimeString()}
                          </span>
                          <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                            {isExpanded ? '▼' : '▶'}
                          </span>
                        </div>
                        <div style={{ fontSize: '11px', color: '#e2e8f0', wordBreak: 'break-all', fontFamily: 'monospace' }}>
                          {req.url}
                        </div>
                        {req.error && !isExpanded && (
                          <div style={{ marginTop: '4px', fontSize: '10px', color: '#f87171' }}>
                            Error: {req.error}
                          </div>
                        )}
                      </div>

                      {/* Expandable details */}
                      {isExpanded && (
                        <div style={{ padding: '12px', borderTop: '1px solid #334155', background: '#0f172a', fontSize: '11px' }}>
                          {/* Request Headers */}
                          {req.requestHeaders && Object.keys(req.requestHeaders).length > 0 && (
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ color: '#3b82f6', fontWeight: '600', marginBottom: '4px' }}>Request Headers:</div>
                              <div style={{ background: '#1e293b', padding: '8px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '10px' }}>
                                {Object.entries(req.requestHeaders).map(([key, value]) => (
                                  <div key={key} style={{ marginBottom: '2px' }}>
                                    <span style={{ color: '#f59e0b' }}>{key}:</span> <span style={{ color: '#94a3b8' }}>{value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Request Body */}
                          {req.requestBody && (
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ color: '#3b82f6', fontWeight: '600', marginBottom: '4px' }}>Request Body:</div>
                              <div style={{ background: '#1e293b', padding: '8px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '10px', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                {typeof req.requestBody === 'string' ? req.requestBody : JSON.stringify(req.requestBody, null, 2)}
                              </div>
                            </div>
                          )}

                          {/* Response Headers */}
                          {req.responseHeaders && Object.keys(req.responseHeaders).length > 0 && (
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ color: '#10b981', fontWeight: '600', marginBottom: '4px' }}>Response Headers:</div>
                              <div style={{ background: '#1e293b', padding: '8px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '10px' }}>
                                {Object.entries(req.responseHeaders).map(([key, value]) => (
                                  <div key={key} style={{ marginBottom: '2px' }}>
                                    <span style={{ color: '#f59e0b' }}>{key}:</span> <span style={{ color: '#94a3b8' }}>{value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Response Body */}
                          {req.responseBody && (
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ color: '#10b981', fontWeight: '600', marginBottom: '4px' }}>Response Body:</div>
                              <div style={{ background: '#1e293b', padding: '8px', borderRadius: '4px', fontFamily: 'monospace', fontSize: '10px', whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: '300px', overflowY: 'auto' }}>
                                {typeof req.responseBody === 'string' ? req.responseBody : JSON.stringify(req.responseBody, null, 2)}
                              </div>
                            </div>
                          )}

                          {/* Error Details */}
                          {req.error && (
                            <div style={{ marginBottom: '12px' }}>
                              <div style={{ color: '#ef4444', fontWeight: '600', marginBottom: '4px' }}>Error:</div>
                              <div style={{ background: '#1e293b', padding: '8px', borderRadius: '4px', color: '#f87171', fontSize: '10px' }}>
                                {req.error}
                                {req.stack && (
                                  <pre style={{ marginTop: '8px', fontSize: '9px', whiteSpace: 'pre-wrap' }}>{req.stack}</pre>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        );

      case 'storage':
        const localStorageItems = Object.entries(localStorage);
        const sessionStorageItems = Object.entries(sessionStorage);
        const cookies = document.cookie.split(';').filter(c => c.trim());
        const storageQuery = storageSearchQuery.trim().toLowerCase();

        const matchesQuery = (key: string, value: string) => {
          if (!storageQuery) return true;
          const content = `${key} ${value}`.toLowerCase();
          return content.includes(storageQuery);
        };

        return (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#0f172a', color: '#e2e8f0', minHeight: 0 }}>
            <div style={{ padding: '10px', borderBottom: '1px solid #334155', background: '#1e293b', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                  Clear all data & reload (localStorage, sessionStorage, cookies)
                </span>
                <button
                  onClick={clearAllData}
                  style={{
                    padding: '4px 12px',
                    borderRadius: '4px',
                    border: 'none',
                    background: '#ef4444',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  Clear All
                </button>
              </div>
              <input
                type="text"
                value={storageSearchQuery}
                onChange={(e) => setStorageSearchQuery(e.target.value)}
                placeholder="Search storage keys or values..."
                style={{
                  width: '100%',
                  padding: '6px 10px',
                  borderRadius: '4px',
                  border: '1px solid #334155',
                  background: '#0f172a',
                  color: '#e2e8f0',
                  fontSize: '12px',
                }}
              />
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '12px', fontFamily: 'monospace', fontSize: '12px' }}>
              <div style={{ marginBottom: '12px' }}>
                <h4 style={{ margin: '0 0 6px 0', color: '#60a5fa', fontSize: '12px' }}>
                  📦 localStorage ({localStorageItems.length})
                </h4>
                {localStorageItems.length === 0 ? (
                  <div style={{ color: '#64748b', padding: '6px', fontSize: '11px' }}>Empty</div>
                ) : (
                  <div style={{ background: '#1e293b', borderRadius: '4px', padding: '6px' }}>
                    {localStorageItems.filter(([key, value]) => matchesQuery(key, value)).map(([key, value]) => (
                      <div key={key} style={{ marginBottom: '4px', paddingBottom: '4px', borderBottom: '1px solid #334155' }}>
                        <button
                          type="button"
                          onClick={() => setSelectedStorageItem({ scope: 'localStorage', key, value })}
                          style={{
                            color: '#fbbf24',
                            fontSize: '10px',
                            fontWeight: '600',
                            background: 'transparent',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          {key}
                        </button>
                        <div style={{ color: '#94a3b8', fontSize: '9px', marginTop: '1px', wordBreak: 'break-all' }}>
                          {value.substring(0, 80)}{value.length > 80 && '...'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '12px' }}>
                <h4 style={{ margin: '0 0 6px 0', color: '#60a5fa', fontSize: '12px' }}>
                  💾 sessionStorage ({sessionStorageItems.length})
                </h4>
                {sessionStorageItems.length === 0 ? (
                  <div style={{ color: '#64748b', padding: '6px', fontSize: '11px' }}>Empty</div>
                ) : (
                  <div style={{ background: '#1e293b', borderRadius: '4px', padding: '6px' }}>
                    {sessionStorageItems.filter(([key, value]) => matchesQuery(key, value)).map(([key, value]) => (
                      <div key={key} style={{ marginBottom: '4px', paddingBottom: '4px', borderBottom: '1px solid #334155' }}>
                        <button
                          type="button"
                          onClick={() => setSelectedStorageItem({ scope: 'sessionStorage', key, value })}
                          style={{
                            color: '#fbbf24',
                            fontSize: '10px',
                            fontWeight: '600',
                            background: 'transparent',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          {key}
                        </button>
                        <div style={{ color: '#94a3b8', fontSize: '9px', marginTop: '1px', wordBreak: 'break-all' }}>
                          {value.substring(0, 80)}{value.length > 80 && '...'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '12px' }}>
                <h4 style={{ margin: '0 0 6px 0', color: '#60a5fa', fontSize: '12px' }}>
                  🍪 Cookies ({cookies.length})
                </h4>
                {cookies.length === 0 ? (
                  <div style={{ color: '#64748b', padding: '6px', fontSize: '11px' }}>Empty</div>
                ) : (
                  <div style={{ background: '#1e293b', borderRadius: '4px', padding: '6px' }}>
                    {cookies.map((cookie, idx) => {
                      const [key, ...rest] = cookie.split('=');
                      const value = rest.join('=');
                      if (!matchesQuery(key.trim(), value)) {
                        return null;
                      }
                      return (
                        <div key={idx} style={{ marginBottom: '4px', paddingBottom: '4px', borderBottom: '1px solid #334155' }}>
                          <button
                            type="button"
                            onClick={() => setSelectedStorageItem({ scope: 'cookies', key: key.trim(), value })}
                            style={{
                              color: '#fbbf24',
                              fontSize: '10px',
                              fontWeight: '600',
                              background: 'transparent',
                              border: 'none',
                              padding: 0,
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                          >
                            {key.trim()}
                          </button>
                          <div style={{ color: '#94a3b8', fontSize: '9px', marginTop: '1px', wordBreak: 'break-all' }}>
                            {value}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {selectedStorageItem && (
                <div style={{ marginBottom: '12px', border: '1px solid #334155', borderRadius: '6px', background: '#1e293b', padding: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#e2e8f0' }}>
                      {selectedStorageItem.scope} ▸ {selectedStorageItem.key}
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            await navigator.clipboard.writeText(selectedStorageItem.value);
                          } catch {
                            // Ignore clipboard errors
                          }
                        }}
                        style={{
                          padding: '3px 8px',
                          borderRadius: '4px',
                          border: '1px solid #334155',
                          background: '#0f172a',
                          color: '#e2e8f0',
                          fontSize: '10px',
                          cursor: 'pointer',
                        }}
                      >
                        Copy
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedStorageItem(null)}
                        style={{
                          padding: '3px 8px',
                          borderRadius: '4px',
                          border: '1px solid #334155',
                          background: '#0f172a',
                          color: '#e2e8f0',
                          fontSize: '10px',
                          cursor: 'pointer',
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                  <div style={{ background: '#0f172a', borderRadius: '4px', padding: '8px', fontSize: '10px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '240px', overflowY: 'auto' }}>
                    {(() => {
                      try {
                        return JSON.stringify(JSON.parse(selectedStorageItem.value), null, 2);
                      } catch {
                        return selectedStorageItem.value;
                      }
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'routing':
        const currentPath = window.location.pathname;
        const currentHost = window.location.hostname;
        const currentOrigin = window.location.origin;

        // Find X-Routed-To headers from recent network requests
        const routedRequests = networkRequests.filter(req => req.responseHeaders?.['x-routed-to']);
        const latestRoutedTo = routedRequests.length > 0
          ? routedRequests[routedRequests.length - 1].responseHeaders?.['x-routed-to']
          : null;

        return (
          <div style={{ flex: 1, overflowY: 'auto', padding: '12px', fontFamily: 'monospace', fontSize: '12px', background: '#0f172a', color: '#e2e8f0' }}>
            {/* Current Route Info */}
            <div style={{ marginBottom: '16px', padding: '12px', background: '#1e293b', borderRadius: '6px', border: '1px solid #334155' }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#3b82f6', fontSize: '13px', fontWeight: '600' }}>
                🧭 Current Route
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '11px' }}>
                <div>
                  <span style={{ color: '#94a3b8' }}>Path:</span>{' '}
                  <span style={{ color: '#e2e8f0', fontWeight: '600' }}>{currentPath}</span>
                </div>
                <div>
                  <span style={{ color: '#94a3b8' }}>Host:</span>{' '}
                  <span style={{ color: '#e2e8f0' }}>{currentHost}</span>
                </div>
                <div>
                  <span style={{ color: '#94a3b8' }}>Origin:</span>{' '}
                  <span style={{ color: '#e2e8f0' }}>{currentOrigin}</span>
                </div>
                {latestRoutedTo && (
                  <div>
                    <span style={{ color: '#94a3b8' }}>Routed To:</span>{' '}
                    <span style={{ color: '#10b981', fontWeight: '600' }}>{latestRoutedTo}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Routing Architecture Info */}
            <div style={{ marginBottom: '16px', padding: '12px', background: '#1e293b', borderRadius: '6px', border: '1px solid #334155' }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#3b82f6', fontSize: '13px', fontWeight: '600' }}>
                🏗️ Multi-Page Architecture
              </h4>
              <div style={{ fontSize: '10px', color: '#94a3b8', lineHeight: '1.6' }}>
                <p style={{ margin: '4px 0' }}>
                  This app uses <span style={{ color: '#10b981', fontWeight: '600' }}>Worker-based Routing</span>:
                </p>
                <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                  <li>Cloudflare Worker acts as a reverse proxy</li>
                  <li>Routes requests to different Pages deployments</li>
                  <li>Each app (home, weather, chat) is independently deployed</li>
                  <li>Check Network tab for <code style={{ color: '#f59e0b' }}>X-Routed-To</code> header</li>
                </ul>
              </div>
            </div>

            {/* X-Routed-To from Network Requests */}
            {routedRequests.length > 0 && (
              <div style={{ marginBottom: '16px', padding: '12px', background: '#1e293b', borderRadius: '6px', border: '1px solid #334155' }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#3b82f6', fontSize: '13px', fontWeight: '600' }}>
                  🔀 Worker Routing Headers
                </h4>
                <div style={{ fontSize: '10px' }}>
                  {routedRequests.slice(-10).reverse().map((req, idx) => (
                    <div key={idx} style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid #334155' }}>
                      <div style={{ color: '#94a3b8' }}>
                        {new Date(req.timestamp).toLocaleTimeString()} - {req.method} {new URL(req.url).pathname}
                      </div>
                      <div style={{ color: '#10b981', fontWeight: '600', marginTop: '2px' }}>
                        → {req.responseHeaders?.['x-routed-to']}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Routing History */}
            <div style={{ marginBottom: '16px', padding: '12px', background: '#1e293b', borderRadius: '6px', border: '1px solid #334155' }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#3b82f6', fontSize: '13px', fontWeight: '600' }}>
                📜 Navigation History ({routingHistory.length})
              </h4>
              {routingHistory.length === 0 ? (
                <div style={{ color: '#64748b', padding: '6px', fontSize: '11px' }}>
                  No navigation recorded yet. Navigate between pages to see history.
                </div>
              ) : (
                <div style={{ fontSize: '10px', maxHeight: '300px', overflowY: 'auto' }}>
                  {[...routingHistory].reverse().map((entry, idx) => (
                    <div key={entry.id} style={{ marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid #334155' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ color: '#94a3b8' }}>
                          {entry.timestamp.toLocaleTimeString()}
                        </span>
                        <span style={{ color: '#e2e8f0', fontWeight: '600' }}>
                          {entry.path}
                        </span>
                      </div>
                      {entry.routedTo && (
                        <div style={{ color: '#10b981', fontSize: '9px', marginTop: '2px' }}>
                          Routed to: {entry.routedTo}
                        </div>
                      )}
                      <div style={{ color: '#64748b', fontSize: '9px', marginTop: '2px' }}>
                        {entry.deployment}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Route Map */}
            <div style={{ padding: '12px', background: '#1e293b', borderRadius: '6px', border: '1px solid #334155' }}>
              <h4 style={{ margin: '0 0 8px 0', color: '#3b82f6', fontSize: '13px', fontWeight: '600' }}>
                🗺️ Expected Route Map
              </h4>
              <div style={{ fontSize: '10px', fontFamily: 'monospace' }}>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#f59e0b' }}>/</span>{' '}
                  <span style={{ color: '#94a3b8' }}>→ room-home.pages.dev</span>
                </div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#f59e0b' }}>/home</span>{' '}
                  <span style={{ color: '#94a3b8' }}>→ room-home.pages.dev</span>
                </div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#f59e0b' }}>/weather</span>{' '}
                  <span style={{ color: '#94a3b8' }}>→ room-weather.pages.dev</span>
                </div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#f59e0b' }}>/chat</span>{' '}
                  <span style={{ color: '#94a3b8' }}>→ room-chat.pages.dev</span>
                </div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#f59e0b' }}>/store</span>{' '}
                  <span style={{ color: '#94a3b8' }}>→ room-store.pages.dev</span>
                </div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#f59e0b' }}>/shell</span>{' '}
                  <span style={{ color: '#94a3b8' }}>→ room-shell.pages.dev</span>
                </div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#f59e0b' }}>/settings</span>{' '}
                  <span style={{ color: '#94a3b8' }}>→ room-settings.pages.dev</span>
                </div>
                <div style={{ marginBottom: '4px' }}>
                  <span style={{ color: '#f59e0b' }}>/credits</span>{' '}
                  <span style={{ color: '#94a3b8' }}>→ room-credits.pages.dev</span>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          position: 'fixed',
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)',
          right: '12px',
          transform: 'none',
          width: isOpen ? '88px' : '44px',
          height: '44px',
          borderRadius: isOpen ? '999px' : '50%',
          border: 'none',
          background: isOpen
            ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.95) 0%, rgba(220, 38, 38, 0.95) 100%)'
            : 'rgba(15, 23, 42, 0.65)',
          color: 'white',
          fontSize: '12px',
          fontWeight: '600',
          cursor: 'pointer',
          boxShadow: '0 1px 6px rgba(0,0,0,0.2)',
          zIndex: 9998,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '4px',
          transition: 'all 0.3s ease',
          letterSpacing: '0.4px',
          opacity: isOpen ? 1 : 0.7,
        }}
        title="Toggle Dev Console (Ctrl+Shift+K)"
      >
        <span style={{ fontSize: '16px' }}>🔧</span>
        {isOpen && <span>CLOSE</span>}
        {(logs.length > 0 || networkStats.errors > 0) && !isOpen && (
          <span style={{
            background: '#ef4444',
            borderRadius: '10px',
            padding: '2px 5px',
            fontSize: '9px',
            fontWeight: '700',
            marginLeft: '2px',
            position: 'absolute',
            top: '-6px',
            right: '-6px',
          }}>
            {logs.filter(l => l.type === 'error').length + networkStats.errors}
          </span>
        )}
      </button>

      {/* Console Panel */}
      {isOpen && (
        <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, height: isFullscreen ? '100vh' : '60vh', background: 'white', borderTop: '2px solid #e5e7eb', zIndex: 9999, display: 'flex', flexDirection: 'column', boxShadow: '0 -4px 12px rgba(0,0,0,0.1)', transition: 'height 0.3s ease' }}>
          {/* Header - Compact */}
          <div style={{ padding: '6px 12px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f9fafb' }}>
            <div />
            <div style={{ display: 'flex', gap: '6px' }}>
              <button
                onClick={() => setIsFullscreen(!isFullscreen)}
                style={{
                  padding: '3px 8px',
                  borderRadius: '3px',
                  border: '1px solid #d1d5db',
                  background: isFullscreen ? '#3b82f6' : 'white',
                  color: isFullscreen ? 'white' : 'inherit',
                  cursor: 'pointer',
                  fontSize: '11px',
                  transition: 'all 0.2s'
                }}
                title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
              >
                {isFullscreen ? '⤓' : '⤢'}
              </button>
              <button onClick={() => setIsOpen(false)} style={{ padding: '3px 8px', borderRadius: '3px', border: 'none', background: '#e5e7eb', cursor: 'pointer', fontSize: '11px' }}>
                ✕
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', background: '#f9fafb', padding: '0 8px', gap: '4px' }}>
            {(['console', 'network', 'storage', 'routing'] as TabType[]).map(tab => {
              const errorCount = logs.filter(l => l.type === 'error').length;
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '6px 12px',
                    border: 'none',
                    background: activeTab === tab ? '#0f172a' : 'transparent',
                    color: activeTab === tab ? '#e2e8f0' : '#6b7280',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: '600',
                    borderRadius: '4px 4px 0 0',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {tab === 'console' && (
                    <>
                      📋 Console
                      {errorCount > 0 && (
                        <span style={{
                          background: '#ef4444',
                          color: 'white',
                          borderRadius: '8px',
                          padding: '1px 5px',
                          fontSize: '9px',
                          fontWeight: '700',
                          minWidth: '16px',
                          textAlign: 'center'
                        }}>
                          {errorCount}
                        </span>
                      )}
                    </>
                  )}
                  {tab === 'network' && (
                    <>
                      🌐 Network
                      {networkStats.errors > 0 && (
                        <span style={{
                          background: '#ef4444',
                          color: 'white',
                          borderRadius: '8px',
                          padding: '1px 5px',
                          fontSize: '9px',
                          fontWeight: '700',
                          minWidth: '16px',
                          textAlign: 'center'
                        }}>
                          {networkStats.errors}
                        </span>
                      )}
                    </>
                  )}
                  {tab === 'storage' && '💾 Storage'}
                  {tab === 'routing' && '🧭 Routing'}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          {renderTabContent()}
        </div>
      )}
    </>
  );
}
